import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SendAuthorizationCodeModel} from '@app/models/dropbox/send-authorization-code.model';
import {CloudDriveProvider} from '@app/models/clouddrive/clouddrive-provider.model';
import {Observable, Subject} from 'rxjs';
import {FileData} from '@app/models/clouddrive/filedata.model';
import {DropboxFolderInfo} from '@app/models/clouddrive/dropbox-folder-info.model';
import {SendOneDriveAuthorizationCodeModel} from '@app/routes/clouddrive/models/send-onedrive-authorization-code.model';
import {OneDriveFolderInfo} from '@app/routes/clouddrive/models/onedrive-folder-info.model';

@Injectable({providedIn: 'root'})
export class CloudDriveService {
    private service = 'api/clouddriveservice';

    public userProviderChanged = new Subject();

    constructor(private httpClient: HttpClient) {
    }

    public getOneDriveAuthenticationUrl() {
        return this.httpClient.get(`${this.service}/onedrive/getAuthenticationUrl`, {responseType: 'text'});
    }

    public sendOneDriveAuthorizationCode(model: SendOneDriveAuthorizationCodeModel) {
        return new Observable((observer) => {
            this.httpClient.post(`${this.service}/onedrive/sendAuthorizationCode`,
                model).subscribe(
                result => {
                    observer.next(result);
                    this.userProviderChanged.next(result);
                },
                error => observer.error(error),
                () => observer.complete());
        });
    }

    public sendDropboxAuthorizationCode(model: SendAuthorizationCodeModel) {
        return new Observable((observer) => {
            this.httpClient.post(`${this.service}/dropbox/sendAuthorizationCode`,
                model).subscribe(
                result => {
                    observer.next(result);
                    this.userProviderChanged.next(result);
                },
                error => observer.error(error),
                () => observer.complete());
        });
    }

    public isDropboxAuthorized() {
        return this.httpClient.get<boolean>(`${this.service}/dropbox/isAuthorized`);
    }

    public getDropboxFolderInfo(path: string) {
        const params = new URLSearchParams();
        params.set('path', path);

        return this.httpClient.get<DropboxFolderInfo>(`${this.service}/dropbox/getFolderInfo`,
            {params: {'path': path}});
    }

    public getDropboxDownloadLink(path: string) {
        return this.httpClient.post<string>(`${this.service}/dropbox/getDownloadLink`, {},
            {params: {'path': path}});
    }

    public getOneDriveFolderInfo(path: string) {
        const params = new URLSearchParams();
        params.set('path', path);

        return this.httpClient.get<OneDriveFolderInfo>(`${this.service}/onedrive/getFolderInfo`,
            {params: {'path': path}});
    }

    public getOneDriveDownloadLink(path: string) {
        return this.httpClient.post<string>(`${this.service}/onedrive/getDownloadLink`, {},
            {params: {'path': path}});
    }

    public revokeToken(provider: CloudDriveProvider) {
        return new Observable((observer) => {
            this.httpClient.delete(`${this.service}/deleteToken/${provider}`).subscribe(
                result => {
                    observer.next(result);
                    this.userProviderChanged.next(result);
                },
                error => observer.error(error),
                () => observer.complete());
        });
    }

    public getUserProvider() {
        return this.httpClient.get<CloudDriveProvider>(`${this.service}/getUserProvider`);
    }

    public authorizeCloud() {
        return new Observable((observer) => {
            this.httpClient.post(`${this.service}/cloud/authorize`, {}).subscribe(
                result => {
                    observer.next(result);
                    this.userProviderChanged.next(result);
                },
                error => observer.error(error),
                () => observer.complete());
        });
    }

    public getRecentlyUploadedFiles() {
        return this.httpClient.get<FileData[]>(`${this.service}/getRecentlyUploadedFiles`);
    }

    public downloadFile(id: number) {
        return this.httpClient.get(`${this.service}/downloadFile/${id}`, {responseType: 'blob'});
    }
}
